import React, {
	useEffect,
	useMemo,
	useCallback,
	type ReactElement,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	type MouseEvent,
	type KeyboardEvent,
	useRef,
} from 'react';
import memoizeOne from 'memoize-one';
import type {
	Page,
	PageUnAuthenticated,
} from '@atlassian/jira-business-confluence-pages/src/common/types/remote-issue-links-pages.tsx';
import {
	COMMENT_SUMMARY_TYPE,
	REMOTE_ISSUE_LINKS_TYPE,
} from '@atlassian/jira-business-constants/src/index.tsx';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/types.tsx';
import { getColumnWidths } from '@atlassian/jira-business-fields/src/common/column-widths/index.tsx';
import { getFieldIcons } from '@atlassian/jira-business-fields/src/common/icon/index.tsx';
import { getSortingDisabledFields } from '@atlassian/jira-business-fields/src/common/sorting/index.tsx';
import type {
	FieldCellProps,
	IssueUpdatedHandler,
} from '@atlassian/jira-business-fields/src/common/types.tsx';
import {
	safeGetTransformedFieldValue,
	type Field,
} from '@atlassian/jira-business-fields/src/utils/index.tsx';
import { useIssueViewIsVisible } from '@atlassian/jira-business-issue-view/src/common/use-issue-view-is-visible/index.tsx';
import {
	categoryIdForStatusCategory,
	DONE,
} from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	CATEGORY_TYPE,
	ISSUE_TYPE,
	SUMMARY_TYPE,
	TEXT_CF_TYPE,
	STATUS_TYPE,
	ASSIGNEE_TYPE,
	REPORTER_TYPE,
	DUE_DATE_TYPE,
	CREATED,
	UPDATED,
	LABELS_TYPE,
	PRIORITY_TYPE,
	URL_CF_TYPE,
	USER_CF_TYPE,
	TEXT_AREA_CF_TYPE,
	SELECT_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	TIME_ESTIMATE_TYPE,
	DATE_CF_TYPE,
	DATETIME_CF_TYPE,
	NUMBER_CF_TYPE,
	MULTI_USER_CF_TYPE,
	COMPONENTS_TYPE,
	STORY_POINTS_TYPE,
	LABELS_CF_TYPE,
	PEOPLE_CF_TYPE,
	COMMENT_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	SPRINT_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { toIssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { SELECT_ROW_COLUMN_WIDTH } from '../../common/constants.tsx';
import type { CellRenderer, CellRendererProps } from '../../common/types/base-table.tsx';
import { loadingCellStyles } from '../../common/ui/loading-cell/index.tsx';
import {
	INLINE_CREATE_ITEM_PREFIX,
	isItemSkeleton,
} from '../../common/utils/row-placeholder/index.tsx';
import {
	COLUMNS_WITH_DISABLED_REORDERING,
	PLACEHOLDER_COLUMN,
	SELECT_ROW_COLUMN_ID,
} from '../../constants.tsx';
import { useCommentsSidebar } from '../../controllers/comments-sidebar/index.tsx';
import { getAlwaysVisibleFields } from '../../controllers/get-issue-fields/constants/index.tsx';
import { useIssueFields } from '../../controllers/get-issue-fields/index.tsx';
import { useHierarchyStore } from '../../controllers/hierarchy/hierarchy-store/index.tsx';
import { useInlineFieldCreateControls } from '../../controllers/inline-field-create-controls/index.tsx';
import type { ListViewControllerData } from '../../controllers/list-view-issues/types.tsx';
import { useSystemFields } from '../../controllers/system-fields/index.tsx';
import { useJiraSettings } from '../../services/get-jira-settings/index.tsx';
import type { TimeTrackingConfig } from '../../services/get-jira-settings/types.tsx';
import AsyncCategoryCell from './category-cell/index.tsx';
import { CellNavigation } from './cell-navigation/index.tsx';
import { CellWithHorizontalVirtualization } from './cell/index.tsx';
import AsyncCheckboxCell from './checkbox-cell/index.tsx';
import { ColumnHeader } from './column-header/index.tsx';
import { CommentsCell } from './comments-cell/index.tsx';
import { CommentsSidebar } from './comments-sidebar/index.tsx';
import AsyncComponentsCell from './components-cell/index.tsx';
import AsyncDateCell from './date-cell/index.tsx';
import AsyncDateTimeCell from './date-time-cell/index.tsx';
import { KeyCell } from './key-cell/index.tsx';
import AsyncLabelsCell from './labels-cell/index.tsx';
import messages from './messages.tsx';
import AsyncMultiSelectCell from './multi-select-cell/index.tsx';
import AsyncMultiUserCell from './multi-user-cell/index.tsx';
import AsyncNumberCell from './number-cell/index.tsx';
import AsyncOriginalEstimateCell from './original-estimate-cell/index.tsx';
import AsyncPriorityCell from './priority-cell/index.tsx';
import AsyncRemoteIssueLinksCell from './remote-issue-links-cell/index.tsx';
import { SelectAllColumnCheckbox, SelectRowCheckbox } from './select-row-cell/index.tsx';
import AsyncSingleSelectCell from './single-select-cell/index.tsx';
import AsyncSprintCell from './sprint-cell/index.tsx';
import AsyncStatusCell from './status-cell/index.tsx';
import { TextAreaCell } from './text-area-cell/index.tsx';
import { TextCell } from './text-cell/index.tsx';
import { TypeCell } from './type-cell/index.tsx';
import { UrlCell } from './url-cell/index.tsx';
import AsyncUserCell from './user-cell/index.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CellRendererParamType = FieldCellProps<ListViewControllerData, any>;

const dateFieldWidth = 120;

const getAlwaysVisibleFieldsMemoized = memoizeOne(getAlwaysVisibleFields);

export const getDefaultColumnWidths = () => ({
	[ISSUE_TYPE]: 110,
	key: 120,
	[SUMMARY_TYPE]: 400,
	[STATUS_TYPE]: 120,
	[ASSIGNEE_TYPE]: 180,
	[REPORTER_TYPE]: 180,
	[USER_CF_TYPE]: 180,
	[DUE_DATE_TYPE]: dateFieldWidth,
	[CREATED]: dateFieldWidth,
	[UPDATED]: dateFieldWidth,
	[PRIORITY_TYPE]: 120,
	[LABELS_TYPE]: 170,
	[SELECT_CF_TYPE]: 120,
	[MULTI_SELECT_CF_TYPE]: 170,
	[URL_CF_TYPE]: 180,
	[MULTI_USER_CF_TYPE]: 200,
	moreFields: 50,
	[DATETIME_CF_TYPE]: 200,
	[COMPONENTS_TYPE]: 170,
	[CATEGORY_TYPE]: 170,
	[COMMENT_TYPE]: 145,
	[COMMENT_SUMMARY_TYPE]: 145,
	[REMOTE_ISSUE_LINKS_TYPE]: 200,
	[SPRINT_TYPE]: 145,
	...(fg('jsw_list_view_-_all_the_fields') ? getColumnWidths() : {}),
});

// sorting disabled for these fields
export const getSortingActions = () => ({
	[TEXT_AREA_CF_TYPE]: false,
	[MULTI_USER_CF_TYPE]: false,
	[PEOPLE_CF_TYPE]: false,
	[COMMENT_TYPE]: false,
	[COMMENT_SUMMARY_TYPE]: false,
	[REMOTE_ISSUE_LINKS_TYPE]: false,
	...(fg('jsw_list_view_-_all_the_fields') ? getSortingDisabledFields() : {}),
});

const COMMON_COLUMN_ATTRIBUTES = {
	frozen: false,
	maxWidth: 640,
	minWidth: 120,
	resizable: true,
};

const COLUMN_ATTRIBUTE_OVERRIDES = {
	[PRIORITY_TYPE]: {
		minWidth: 52,
	},
	[REMOTE_ISSUE_LINKS_TYPE]: {
		minWidth: 200,
	},
	key: {
		minWidth: 72,
	},
};

const selectRowColumn = (showBulkEditOnboarding: boolean) => ({
	key: SELECT_ROW_COLUMN_ID,
	dataKey: SELECT_ROW_COLUMN_ID,
	width: SELECT_ROW_COLUMN_WIDTH,
	headerRenderer: <SelectAllColumnCheckbox showBulkEditOnboarding={showBulkEditOnboarding} />,
	cellRenderer: (props: CellRendererProps) => {
		if (isItemSkeleton(props.rowData.issueId)) {
			// experimenting with inlining CSS here to avoid styled-components runtime perf overhead
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			return <div style={loadingCellStyles} />;
		}

		return (
			<CellNavigation columnIndex={0} rowIndex={props.rowIndex || 0}>
				<SelectRowCheckbox {...props} />
			</CellNavigation>
		);
	},
});

const iconSize = 'medium';
const EMPTY_ARRAY: BusinessIssueField[] = [];
const EMPTY_PAGES_ARRAY: (Page | PageUnAuthenticated)[] = [];

const isTimeTrackingConfigNeeded = (field: BusinessIssueField) => field.type === TIME_ESTIMATE_TYPE;

type useGetColumnsArgs = {
	columnWidths: Record<string, number>;
	rowHeight: number;
	openIssueModal: (arg1: { id: string; key: string }) => (arg1: MouseEvent | KeyboardEvent) => void;
	highlightRow: (arg1: string) => void;
	loading: boolean;
	projectId: string;
	onIssueUpdated: IssueUpdatedHandler;
	showBulkEditOnboarding: boolean;
};

type UseGetColumnsReturn = {
	maxWidth: number;
	minWidth: number;
	resizable: boolean;
	key: string;
	dataKey: string;
	width: number;
	timeTrackingConfig: TimeTrackingConfig | undefined;
	headerRenderer: ReactElement<ComponentProps<typeof ColumnHeader>>;
	frozen: 'left' | 'right' | boolean;
	cellRenderer: CellRenderer;
}[];

export const useGetColumns = ({
	columnWidths,
	rowHeight,
	openIssueModal,
	highlightRow,
	loading: pageLoading,
	projectId,
	onIssueUpdated,
	showBulkEditOnboarding = false,
}: useGetColumnsArgs): UseGetColumnsReturn => {
	const { hierarchyLevelsWithCache, getVisibleFields, loading, isVisibleFieldsCached } =
		useIssueFields();
	const visibleFields = getVisibleFields();
	const iconMap = getFieldIcons(iconSize);
	const { data: projectData, getProjectData, loading: projectDataLoading } = useJiraSettings();
	const [, { getParentIssueKeysByChildId }] = useHierarchyStore();
	const { globalSystemFields } = useSystemFields();

	const { onOpenCommentsSidebar, selectedIssueKey } = useCommentsSidebar({
		CommentsSidebar,
	});
	const isIssueViewVisible = useIssueViewIsVisible();
	const [{ createFields }] = useInlineFieldCreateControls();
	const [hierarchyStore] = useHierarchyStore();
	const currentFirstRowKey = useRef<string | undefined | null>();
	const project = useProject();

	const defaultColumnWidths = useMemo(() => getDefaultColumnWidths(), []);
	const sortingActions = useMemo(() => getSortingActions(), []);

	const columnFields = useMemo(() => {
		const _columnFields = [...visibleFields];
		if (createFields.length > 0) {
			createFields.forEach(({ fieldId, name, type, createIndex }) => {
				const placeholderField: BusinessIssueField = {
					id: `${PLACEHOLDER_COLUMN}-${fieldId}`,
					name,
					type,
				};
				if (createIndex != null) {
					_columnFields.splice(createIndex, 0, placeholderField);
				} else {
					_columnFields.push(placeholderField);
				}
			});
		}
		return _columnFields;
	}, [visibleFields, createFields]);

	// This function finds the first issue row from the top within the first group item
	const getFirstGroupChildRowKey = useCallback(() => {
		const groups = hierarchyStore.hierarchyData.data;
		const firstGroupChild = groups[0]?.children;

		if (!Array.isArray(groups) || !firstGroupChild) {
			return null;
		}
		const firstGroupRow =
			firstGroupChild.length > 0 && firstGroupChild[0].key.startsWith(INLINE_CREATE_ITEM_PREFIX)
				? firstGroupChild[1]
				: firstGroupChild[0];
		const firstGroupChildRowKey = firstGroupRow?.key ?? null;

		return firstGroupChildRowKey;
	}, [hierarchyStore.hierarchyData.data]);

	useEffect(() => {
		const firstGroupChildRowKey = getFirstGroupChildRowKey();
		currentFirstRowKey.current = firstGroupChildRowKey;
	}, [getFirstGroupChildRowKey]);

	useEffect(() => {
		// defer loading project data until estimate column is visible
		if (
			!projectData &&
			visibleFields.find((field) => field.type === TIME_ESTIMATE_TYPE) &&
			!projectDataLoading &&
			pageLoading === false
		) {
			getProjectData();
		}
	}, [projectData, pageLoading, getProjectData, visibleFields, projectDataLoading]);

	const hierarchyLevelsByName = useMemo(
		() =>
			Object.fromEntries(
				hierarchyLevelsWithCache.map((hierarchyLevel) => [
					hierarchyLevel.name,
					hierarchyLevel.level,
					hierarchyLevel.id,
				]),
			),
		[hierarchyLevelsWithCache],
	);

	const cellRenderers = useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const toReturn: Record<string, any> = {};

		columnFields
			// eslint-disable-next-line complexity
			.map((field) => {
				switch (field.type) {
					case ISSUE_TYPE:
						return {
							[field.id]: ({ cellData = {}, rowData }: CellRendererParamType) => (
								<TypeCell
									id={rowData.issueId}
									iconUrl={cellData.iconUrl}
									name={cellData.name}
									avatarId={field.fieldConfig?.avatarId}
									onChildCreateButtonClicked={rowData.onChildCreateButtonClicked}
									hierarchyLevel={hierarchyLevelsByName[rowData.issuetype.name ?? '']}
									rankIndex={rowData.rankIndex}
								/>
							),
						};
					case 'key':
						return {
							[field.id]: ({ cellData, rowData }: CellRendererParamType) => {
								const parentIssues = getParentIssueKeysByChildId(rowData.issueId);

								return (
									<KeyCell
										issueId={rowData.id}
										issueKey={cellData}
										parentIssues={parentIssues}
										onOpenIssueModal={openIssueModal}
									/>
								);
							},
						};
					case SUMMARY_TYPE:
						return {
							[field.id]: ({ cellData, rowData, rowIndex }: CellRendererParamType) => {
								const isNudgeCell = rowIndex === 0 || rowData?.key === currentFirstRowKey.current;

								return (
									<TextCell
										isNudgeCell={
											fg('jwm_list_new_copy_paste_behaviour')
												? !isIssueViewVisible && isNudgeCell
												: isNudgeCell
										}
										issueId={rowData.issueId}
										issueKey={toIssueKey(rowData.key)}
										fieldType={SUMMARY_TYPE}
										fieldValue={cellData}
										fieldKey={field.id}
										isEditable={field.fieldConfig?.isEditable ?? false}
										testId="business-list.ui.list-view.summary-cell"
									/>
								);
							},
						};
					case URL_CF_TYPE:
						return {
							[field.id]: ({ rowData }: CellRendererParamType) => {
								const url = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'url',
									null,
								);
								return (
									<UrlCell
										issueKey={toIssueKey(rowData.key)}
										fieldType={URL_CF_TYPE}
										fieldValue={url}
										fieldKey={field.id}
										isEditable={field.fieldConfig?.isEditable ?? false}
										fieldName={field.name}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
									/>
								);
							},
						};
					case TEXT_CF_TYPE:
						return {
							[field.id]: ({ rowData }: CellRendererParamType) => {
								const wiki = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'wiki',
									null,
								);
								return (
									<TextCell
										issueId={rowData.issueId}
										issueKey={toIssueKey(rowData.key)}
										fieldType={TEXT_CF_TYPE}
										fieldValue={wiki}
										fieldKey={field.id}
										isEditable={field.fieldConfig?.isEditable ?? false}
										fieldName={field.name}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
										testId="business-list.ui.list-view.text-cf-cell"
									/>
								);
							},
						};
					case TEXT_AREA_CF_TYPE:
						return {
							[field.id]: ({ rowData }: CellRendererParamType) => {
								const adf = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'adf',
									null,
								);
								return (
									<TextAreaCell
										fieldKey={field.id}
										fieldType={TEXT_AREA_CF_TYPE}
										issueKey={toIssueKey(rowData.key)}
										onClick={openIssueModal(rowData)}
										fieldValue={adf}
									/>
								);
							},
						};
					case STATUS_TYPE:
						return {
							[field.id]: ({ cellData, rowData, column }: CellRendererParamType) => (
								<AsyncStatusCell
									issueKey={toIssueKey(rowData.key)}
									width={column.width}
									height={rowHeight}
									rowData={rowData}
									onHighlightRow={highlightRow}
									{...cellData}
								/>
							),
						};
					case ASSIGNEE_TYPE:
						return {
							[field.id]: ({ rowData, column }: CellRendererParamType) => {
								const userField = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'userValue',
									undefined,
								);
								return (
									<AsyncUserCell
										fieldKey={field.id}
										fieldType={ASSIGNEE_TYPE}
										issueKey={toIssueKey(rowData.key)}
										initialValue={userField}
										width={column.width}
										emptyUserNameText={messages.unassignedOption}
										loggedInUserActionNameText={messages.assignToMe}
										testId="business-list.ui.list-view.assignee-cell"
										fieldName={field.name}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
									/>
								);
							},
						};
					case COMMENT_TYPE:
					case COMMENT_SUMMARY_TYPE:
						return {
							[field.id]: ({ rowData }: CellRendererParamType) => {
								const totalCount = rowData?.commentSummary?.totalCount;
								return (
									<CommentsCell
										issueKey={toIssueKey(rowData.key)}
										isSelected={selectedIssueKey === rowData.key}
										onClick={onOpenCommentsSidebar}
										totalCount={totalCount}
									/>
								);
							},
						};
					case REPORTER_TYPE:
						return {
							[field.id]: ({ rowData, column }: CellRendererParamType) => {
								const userField = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'userValue',
									undefined,
								);
								return (
									<AsyncUserCell
										fieldKey={field.id}
										fieldType={REPORTER_TYPE}
										issueKey={toIssueKey(rowData.key)}
										initialValue={userField}
										width={column.width}
										emptyUserNameText={messages.unassignedOption}
										loggedInUserActionNameText={messages.assignToMe}
										isEditable={field.fieldConfig?.isEditable ?? false}
										fieldName={field.name}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
									/>
								);
							},
						};
					case USER_CF_TYPE:
						return {
							[field.id]: ({ rowData, column }: CellRendererParamType) => {
								const userField = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'selectedUser',
									undefined,
								);
								return (
									<AsyncUserCell
										fieldKey={field.id}
										fieldType={USER_CF_TYPE}
										issueKey={toIssueKey(rowData.key)}
										initialValue={userField}
										width={column.width}
										emptyUserNameText={messages.unassignedOption}
										loggedInUserActionNameText={messages.assignToMe}
										// @ts-expect-error - TS2339 - Property 'isEditable' does not exist on type 'never'.
										isEditable={userField?.isEditable}
										fieldName={field.name}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
									/>
								);
							},
						};
					case LABELS_CF_TYPE:
						return {
							[field.id]: ({ rowData, column }: CellRendererParamType) => {
								const labels = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'labels',
									[],
								);
								return (
									<AsyncLabelsCell
										fieldKey={field.id}
										fieldType={LABELS_CF_TYPE}
										issueKey={toIssueKey(rowData.key)}
										issueId={rowData.issueId}
										issueLabels={labels}
										width={column.width}
										isEditable={field.fieldConfig?.isEditable ?? false}
										fieldName={field.name}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
									/>
								);
							},
						};
					case LABELS_TYPE:
						return {
							[field.id]: ({ cellData, rowData, column }: CellRendererParamType) => (
								<AsyncLabelsCell
									fieldKey={field.id}
									fieldType={LABELS_TYPE}
									issueKey={toIssueKey(rowData.key)}
									issueId={rowData.issueId}
									issueLabels={cellData}
									width={column.width}
									isEditable={field.fieldConfig?.isEditable ?? false}
									fieldName={field.name}
									issueType={rowData.issuetype.name}
									issueTypeId={rowData.issuetype.id}
								/>
							),
						};
					case PRIORITY_TYPE:
						return {
							[field.id]: ({ cellData, rowData, column }: CellRendererParamType) => {
								if (fg('jwm_list_new_copy_paste_behaviour')) {
									const priorityValue = safeGetTransformedFieldValue(
										rowData.fields,
										field.id,
										'priority',
										{},
									);

									const { name, id } = rowData.issuetype;

									if (typeof name !== 'string' || typeof id !== 'string') {
										return;
									}

									return (
										<AsyncPriorityCell
											{...priorityValue}
											fieldType={PRIORITY_TYPE}
											issueKey={toIssueKey(rowData.key)}
											width={column.width}
											height={rowHeight}
											fieldName={field.name}
											issueType={name}
											issueTypeId={id}
											testId="business-list.ui.list-view.priority-cell"
											isEditable={field.fieldConfig?.isEditable ?? false}
											selectOptions={field.fieldConfig?.options ?? []}
										/>
									);
								}

								return (
									<AsyncPriorityCell
										{...cellData}
										fieldType={PRIORITY_TYPE}
										issueKey={toIssueKey(rowData.key)}
										width={column.width}
										height={rowHeight}
										fieldName={field.name}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
										testId="business-list.ui.list-view.priority-cell"
										isEditable={field.fieldConfig?.isEditable ?? false}
										selectOptions={field.fieldConfig?.options ?? []}
									/>
								);
							},
						};
					case DUE_DATE_TYPE:
						return {
							[field.id]: ({ cellData, rowData }: CellRendererParamType) => (
								<AsyncDateCell
									issueId={rowData.issueId}
									fieldKey={field.id}
									dateOnly={cellData}
									fieldType={DUE_DATE_TYPE}
									issueKey={toIssueKey(rowData.key)}
									isDue={
										String(rowData.status?.categoryId) !== String(categoryIdForStatusCategory(DONE))
									}
									testId="business-list.ui.list-view.date-cell"
									fieldName={field.name}
									issueType={rowData.issuetype.name}
									issueTypeId={rowData.issuetype.id}
								/>
							),
						};
					case CREATED:
					case UPDATED:
						return {
							[field.id]: ({ cellData, rowData }: CellRendererParamType) => (
								<AsyncDateCell
									issueId={rowData.issueId}
									fieldKey={field.id}
									readOnly
									dateOnly={cellData}
									onOpenIssueModal={openIssueModal}
									rowData={rowData}
									fieldName={field.name}
									issueType={rowData.issuetype.name}
									issueTypeId={rowData.issuetype.id}
								/>
							),
						};
					case SELECT_CF_TYPE:
						return {
							[field.id]: ({ rowData, column }: CellRendererParamType) => {
								const selectedValue = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'selectedValue',
									undefined,
								);
								return (
									<AsyncSingleSelectCell
										fieldType={SELECT_CF_TYPE}
										fieldName={field.name}
										width={column.width}
										fieldKey={field.id}
										issueKey={toIssueKey(rowData.key)}
										issueId={rowData.issueId}
										fieldValue={selectedValue}
										isEditable={field.fieldConfig?.isEditable ?? false}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
									/>
								);
							},
						};
					case MULTI_SELECT_CF_TYPE:
						return {
							[field.id]: ({ rowData, column }: CellRendererParamType) => {
								const selectedValues = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'selectedValues',
									undefined,
								);
								return (
									<AsyncMultiSelectCell
										fieldType={MULTI_SELECT_CF_TYPE}
										fieldName={field.name}
										selectOptions={field.fieldConfig?.selectOptions}
										width={column.width}
										fieldKey={field.id}
										issueKey={toIssueKey(rowData.key)}
										issueId={rowData.issueId}
										fieldValue={selectedValues}
										isEditable={field.fieldConfig?.isEditable ?? false}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
									/>
								);
							},
						};
					case CATEGORY_TYPE:
						return {
							[field.id]: ({ rowData, column }: CellRendererParamType) => {
								const getFieldValue = () =>
									safeGetTransformedFieldValue(
										// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
										rowData.fields as Field[],
										field.id,
										'value',
										null,
									);

								return (
									<AsyncCategoryCell
										issueId={rowData.issueId}
										issueKey={toIssueKey(rowData.key)}
										width={column.width}
										fieldKey={field.id}
										fieldType={CATEGORY_TYPE}
										issueTypeId={
											rowData.issuetype.id != null ? Number.parseInt(rowData.issuetype.id, 10) : -1
										}
										isEditable
										fieldValue={getFieldValue()}
										fieldName={field.name}
										issueType={rowData.issuetype?.name}
									/>
								);
							},
						};
					case TIME_ESTIMATE_TYPE:
						return {
							[field.id]: ({ rowData }: CellRendererParamType) => {
								const originalEstimate = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'originalEstimate',
									null,
								);
								return (
									<AsyncOriginalEstimateCell
										config={projectData?.jiraSettings?.timeTracking ?? undefined}
										isEditable={field.fieldConfig?.isEditable ?? false}
										fieldId={field.id}
										issueKey={toIssueKey(rowData.key)}
										issueId={rowData.issueId}
										fieldValue={originalEstimate}
									/>
								);
							},
						};
					case DATE_CF_TYPE:
						return {
							[field.id]: ({ rowData }: CellRendererParamType) => {
								const stringValue = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'stringValue',
									null,
								);
								return (
									rowData && (
										<AsyncDateCell
											issueId={rowData.issueId}
											issueKey={toIssueKey(rowData.key)}
											fieldKey={field.id}
											readOnly={!field.fieldConfig?.isEditable}
											fieldType={DATE_CF_TYPE}
											fieldValue={stringValue}
											fieldName={field.name}
											issueType={rowData.issuetype.name}
											issueTypeId={rowData.issuetype.id}
										/>
									)
								);
							},
						};
					case NUMBER_CF_TYPE:
					case STORY_POINTS_TYPE:
					case STORY_POINT_ESTIMATE_CF_TYPE:
						return {
							[field.id]: ({ rowData }: CellRendererParamType) => {
								const numberField = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'floatValue',
									undefined,
								);
								return (
									<AsyncNumberCell
										issueKey={toIssueKey(rowData.key)}
										fieldKey={field.id}
										fieldType={field.type}
										isEditable={field.fieldConfig?.isEditable ?? false}
										// @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type 'number | null'.
										fieldValue={numberField}
										fieldName={field.name}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
									/>
								);
							},
						};
					case DATETIME_CF_TYPE:
						return {
							[field.id]: ({ rowData, column }: CellRendererParamType) => {
								const dateTimeField = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'dateTimeValue',
									undefined,
								);
								return (
									<AsyncDateTimeCell
										height={rowHeight}
										width={column.width}
										issueKey={toIssueKey(rowData.key)}
										fieldKey={field.id}
										isEditable={field.fieldConfig?.isEditable ?? false}
										fieldValue={dateTimeField}
										fieldName={field.name}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
									/>
								);
							},
						};
					case MULTI_USER_CF_TYPE:
					case PEOPLE_CF_TYPE:
						return {
							[field.id]: ({ rowData, column }: CellRendererParamType) => {
								const multiUserField = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									field.type === MULTI_USER_CF_TYPE ? 'selectedUsers' : 'users',
									undefined,
								);
								const fieldType = field.type;
								if (fieldType == null) {
									return null;
								}

								return (
									<AsyncMultiUserCell
										height={rowHeight}
										fieldKey={field.id}
										fieldType={fieldType}
										issueKey={toIssueKey(rowData.key)}
										initialValue={multiUserField}
										width={column.width}
										isEditable={field.fieldConfig?.isEditable ?? false}
										fieldName={field.name}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
									/>
								);
							},
						};
					case COMPONENTS_TYPE:
						return {
							[field.id]: ({ rowData, column }: CellRendererParamType) => {
								const componentsField = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'componentValues',
									undefined,
								);

								return (
									<AsyncComponentsCell
										width={column.width}
										issueKey={toIssueKey(rowData.key)}
										fieldKey={field.id}
										fieldValue={componentsField}
										isEditable={field.fieldConfig?.isEditable ?? false}
										issueId={rowData.issueId}
									/>
								);
							},
						};
					case REMOTE_ISSUE_LINKS_TYPE:
						return {
							[field.id]: ({ rowData }: CellRendererParamType) => (
								<AsyncRemoteIssueLinksCell
									issueKey={toIssueKey(rowData.key)}
									issueId={rowData.issueId}
									pages={rowData.confluenceLinks?.linkedConfluencePages?.pages || EMPTY_PAGES_ARRAY}
									pageCount={rowData.confluenceLinks?.linkedConfluencePages?.pageCount || 0}
								/>
							),
						};
					case MULTI_CHECKBOXES_CF_TYPE:
						return {
							[field.id]: ({ rowData, column }: CellRendererParamType) => {
								const selectedValues = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'selectedValues',
									null,
								);

								return (
									<AsyncCheckboxCell
										width={column.width}
										issueKey={toIssueKey(rowData.key)}
										fieldKey={field.id}
										fieldName={field.name}
										fieldValue={selectedValues}
										isEditable={field.fieldConfig?.isEditable ?? false}
										issueId={rowData.issueId}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
									/>
								);
							},
						};
					case SPRINT_TYPE:
						return {
							[field.id]: ({ rowData, column }: CellRendererParamType) => {
								const selectedValues = safeGetTransformedFieldValue(
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									rowData.fields as Field[],
									field.id,
									'sprint',
									null,
								);

								return (
									<AsyncSprintCell
										width={column.width}
										issueKey={toIssueKey(rowData.key)}
										fieldKey={field.id}
										fieldName={field.name}
										fieldValue={selectedValues}
										isEditable={field.fieldConfig?.isEditable ?? false}
										issueId={rowData.issueId}
										issueType={rowData.issuetype.name}
										issueTypeId={rowData.issuetype.id}
										issueTypeLevel={rowData.issuetype.level}
									/>
								);
							},
						};
					default:
						return { [field.id]: undefined };
				}
			})
			.forEach(
				(fieldEntry) =>
					fieldEntry &&
					Object.keys(fieldEntry).forEach((fieldKey) => {
						toReturn[fieldKey] = fieldEntry[fieldKey];
					}),
			);
		return toReturn;
	}, [
		columnFields,
		hierarchyLevelsByName,
		getParentIssueKeysByChildId,
		openIssueModal,
		isIssueViewVisible,
		rowHeight,
		highlightRow,
		selectedIssueKey,
		onOpenCommentsSidebar,
		projectData?.jiraSettings?.timeTracking,
	]);

	const iconMapWithCategoriesEnabled = useCallback(
		(field: BusinessIssueField) => {
			if (field.type === ISSUE_TYPE) {
				return null;
			}

			if (field.type === CATEGORY_TYPE) {
				return field.type != null && iconMap[field.type];
			}

			// This should come after the check for category type
			// Since category has a customfield_X id which is different across projects we should not check for id
			return iconMap[field.id] ?? (field.type !== undefined && iconMap[field.type]);
		},
		[iconMap],
	);

	const canEditField = project.isSimplified && project.permissions?.administerProject;

	return useMemo(
		() =>
			loading && !isVisibleFieldsCached
				? EMPTY_ARRAY
				: [
						selectRowColumn(showBulkEditOnboarding),
						...columnFields.map((field, index) => {
							const width =
								columnWidths[field.id] ??
								// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly issuetype: 120 | 90; readonly key: 120; readonly summary: 400; readonly status: 120; readonly assignee: 180; readonly reporter: 180; readonly "com.atlassian.jira.plugin.system.customfieldtypes:userpicker": 180; ... 12 more ...; readonly "com.atlassian.jira.plugin.system.customfieldtypes:jwm-category": 170...'.
								(field.type !== undefined && defaultColumnWidths[field.type]) ??
								120;
							return {
								key: field.id,
								dataKey: field.id,
								width,
								/*
								 * https://autodesk.github.io/react-base-table/docs/get-started
								 * To avoid "unnecessary" re-renders, BaseTable ignores functions when comparing column definition by default.
								 * Hence, external values not passed as inputs to the renderer function need to be specified as props here. This was causing
								 * the time original estimate cell to display stale data since the config data is fetched async after initial render.
								 */
								timeTrackingConfig: isTimeTrackingConfigNeeded(field)
									? projectData?.jiraSettings?.timeTracking
									: undefined,
								headerRenderer: (
									<ColumnHeader
										icon={
											// TODO: JET-3805-Replace mocked data with real BE data
											iconMapWithCategoriesEnabled(field)
										}
										label={field.name}
										fieldId={field.id}
										fieldType={field.type}
										canSortField={
											field.type !== undefined &&
											// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly "com.atlassian.jira.plugin.system.customfieldtypes:textarea": false; readonly "com.atlassian.jira.plugin.system.customfieldtypes:multiuserpicker": false; readonly "com.atlassian.jira.plugin.system.customfieldtypes:people": false; }'.
											(sortingActions[field.type] ?? true) &&
											!field.id.startsWith(PLACEHOLDER_COLUMN)
										}
										canHideField={
											!getAlwaysVisibleFieldsMemoized().includes(field.id) &&
											!field.id.startsWith(PLACEHOLDER_COLUMN)
										}
										canEditField={
											!!(
												canEditField &&
												globalSystemFields.length &&
												!globalSystemFields.includes(field.id) &&
												!field.id.startsWith(PLACEHOLDER_COLUMN)
											)
										}
										disableDrag={
											COLUMNS_WITH_DISABLED_REORDERING.includes(field.id) ||
											field.id.startsWith(PLACEHOLDER_COLUMN)
										}
										index={index}
									/>
								),
								cellRenderer: (params: CellRendererParamType) => (
									<CellWithHorizontalVirtualization
										{...params}
										cellRenderers={cellRenderers}
										field={field}
										key={params.column.key}
										onIssueUpdated={onIssueUpdated}
										projectId={projectId}
										width={width}
									/>
								),
								...COMMON_COLUMN_ATTRIBUTES,
								// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly priority: { readonly minWidth: 52; }; readonly key: { readonly minWidth: 72; }; readonly issuetype?: { resizable: boolean; minWidth: number; } | undefined; }'.
								...COLUMN_ATTRIBUTE_OVERRIDES[field.id],
							};
						}),
					],
		[
			loading,
			isVisibleFieldsCached,
			showBulkEditOnboarding,
			columnFields,
			columnWidths,
			defaultColumnWidths,
			projectData?.jiraSettings?.timeTracking,
			iconMapWithCategoriesEnabled,
			sortingActions,
			canEditField,
			globalSystemFields,
			cellRenderers,
			onIssueUpdated,
			projectId,
		],
	);
};
